import { ContentAttached, ContentDetach, DocumentRecordPerson } from '@fs/zion-icon'
import { colors } from '@fs/zion-ui'

const getStatus = (attachmentId, matchId) => {
  if (attachmentId) return 'attached'
  if (matchId) return 'match'
  return 'none'
}

const ICON_MAP = {
  attached: ContentAttached,
  match: DocumentRecordPerson,
  none: ContentDetach,
}

const COLOR_MAP = {
  attached: colors.green10,
  match: colors.blue10,
  none: colors.gray10,
}

const ICON_NAME_MAP = {
  attached: 'ContentAttached',
  match: 'DocumentRecordPerson',
  none: 'ContentDetach',
}

/**
 * Returns the appropriate icon component based on attachment status.
 *
 * @param {string|object|null} attachmentId
 * @param {string|object|null} matchId
 * @returns {Function} Icon component (ContentAttached, DocumentRecordPerson, or ContentDetach)
 */
export const getIconByAttachmentStatus = (attachmentId, matchId) => ICON_MAP[getStatus(attachmentId, matchId)]

/**
 * Returns the background color for the icon based on attachment status.
 *
 * @param {string|object|null} attachmentId
 * @param {string|object|null} matchId
 * @returns {string} Background color (green10, blue10, or gray10)
 */
export const getIconBackgroundColor = (attachmentId, matchId) => COLOR_MAP[getStatus(attachmentId, matchId)]

/**
 * Generates a test ID for the icon element based on attachment status.
 *
 * @param {string|object|null} attachmentId
 * @param {string|object|null} matchId
 * @returns {string} Test ID in format: source-linker-button-{id}-{iconName}
 */
export const getIconTestId = (attachmentId, matchId) => {
  const status = getStatus(attachmentId, matchId)
  const iconName = ICON_NAME_MAP[status]
  let id = 'none'
  if (typeof attachmentId === 'string') {
    id = attachmentId
  } else if (typeof matchId === 'string') {
    id = matchId
  }
  return `source-linker-button-${id}-${iconName}`
}

/**
 * Returns the attachment status text for display as a subheading
 *
 * @param {string|object|null} attachmentId - The ID of the attachment, if one exists
 * @param {string|object|null} matchId - The ID of a potential match, if one exists
 * @param {Function} t - Translation function
 * @returns {string} - The attachment status text
 */
export const getAttachmentStatusText = (attachmentId, matchId, t) => {
  if (attachmentId) {
    // Handle both string and object attachment IDs
    let shortId = attachmentId
    if (typeof attachmentId === 'string' && attachmentId.includes('/')) {
      shortId = attachmentId.split('/').pop() || attachmentId
    } else if (typeof attachmentId === 'object' && attachmentId !== null) {
      // If it's an object, try to extract a meaningful ID
      shortId = attachmentId.id || attachmentId.personId || 'Tree'
    }
    return t('select-person.attachment-attached', 'Attached to {pid}', { pid: shortId })
  }
  if (matchId) {
    return t('select-person.attachment-possible-match', 'Possible Match')
  }
  return t('select-person.attachment-none', 'No Attachment')
}
